import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function LeadSources() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [analytics, setAnalytics] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'online', description: '', cost: '' });
  const [showMerge, setShowMerge] = useState(null);
  const [mergeTarget, setMergeTarget] = useState('');

  const fetchSources = useCallback(() => {
    if (!companyId || companyId === 'holding') return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/leads/sources`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setSources(Array.isArray(d) ? d : []))
      .catch(() => setSources([]))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  useEffect(() => {
    if (!companyId || companyId === 'holding') return;
    fetch(`${API}/companies/${companyId}/leads/sources/performance`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(d => setAnalytics(d)).catch(() => {});
  }, [companyId]);

  const saveSource = () => {
    const url = editingId
      ? `${API}/companies/${companyId}/leads/sources/${editingId}`
      : `${API}/companies/${companyId}/leads/sources`;
    const method = editingId ? 'PUT' : 'POST';

    fetch(url, {
      method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }).then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(() => {
        setShowCreate(false); setEditingId(null);
        setForm({ name: '', category: 'online', description: '', cost: '' });
        fetchSources();
      })
      .catch(() => window.alert('Failed to save source'));
  };

  const deleteSource = (id) => {
    if (!window.confirm('Delete this source? Only draft sources can be deleted.')) return;
    fetch(`${API}/companies/${companyId}/leads/sources/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    }).then(r => { if (!r.ok) throw new Error(); fetchSources(); })
      .catch(() => window.alert('Cannot delete source with assigned leads'));
  };

  const mergeSources = () => {
    if (!showMerge || !mergeTarget) return;
    fetch(`${API}/companies/${companyId}/leads/sources/merge`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_ids: [showMerge], target_id: parseInt(mergeTarget) }),
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) { setShowMerge(null); setMergeTarget(''); fetchSources(); } })
      .catch(() => window.alert('Merge failed'));
  };

  const startEdit = (s) => {
    setForm({ name: s.name, category: s.category, description: s.description || '', cost: s.cost || '' });
    setEditingId(s.id);
    setShowCreate(true);
  };

  if (!companyId || companyId === 'holding') return <div style={{ padding: 32 }}><h2>Select a company</h2></div>;

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const categoryColors = { online: '#3b82f6', offline: '#f59e0b', referral: '#22c55e', partner: '#8b5cf6', direct: '#6b7280', other: '#9ca3af' };

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Lead Sources</h2>
        <button onClick={() => { setEditingId(null); setForm({ name: '', category: 'online', description: '', cost: '' }); setShowCreate(true); }}
          style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer' }}>+ New Source</button>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          {(Array.isArray(analytics) ? analytics : []).slice(0, 6).map((a, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,.08)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{a.name || a.source_name || 'Source'}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: '#6b7280' }}>Leads: <strong>{a.total_leads || 0}</strong></span>
                <span style={{ color: '#22c55e' }}>Won: <strong>{a.won_count || 0}</strong></span>
              </div>
              {a.conversion_rate !== undefined && <div style={{ fontSize: 12, color: '#6366f1', marginTop: 4 }}>Conversion: <strong>{a.conversion_rate}%</strong></div>}
            </div>
          ))}
        </div>
      )}

      {/* Sources Table */}
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              {['Code', 'Name', 'Category', 'Status', 'Leads', 'Cost', 'Actions'].map((h, i) => (
                <th key={i} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {initialLoading ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</td></tr>
            ) : sources.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No sources found</td></tr>
            ) : sources.map(s => (
              <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '10px 14px', color: '#6366f1', fontWeight: 500 }}>{s.source_code}</td>
                <td style={{ padding: '10px 14px', fontWeight: 600 }}>{s.name}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', background: categoryColors[s.category] || '#6b7280' }}>
                    {s.category}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: s.status === 'active' ? '#22c55e' : '#9ca3af', background: s.status === 'active' ? '#f0fdf4' : '#f9fafb' }}>
                    {s.status}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>{s.lead_count || 0}</td>
                <td style={{ padding: '10px 14px' }}>{s.cost ? '$' + Number(s.cost).toLocaleString() : '—'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => startEdit(s)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #6366f1', color: '#6366f1', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => setShowMerge(s.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #f59e0b', color: '#f59e0b', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>Merge</button>
                    <button onClick={() => deleteSource(s.id)} style={{ fontSize: 12, padding: '4px 10px', border: '1px solid #ef4444', color: '#ef4444', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 480 }}>
            <h3 style={{ marginBottom: 20 }}>{editingId ? 'Edit Source' : 'New Source'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Name *</label><input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Category</label>
                <select style={inputStyle} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                  {['online', 'offline', 'referral', 'partner', 'direct', 'other'].map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Description</label><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Cost</label><input style={inputStyle} type="number" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowCreate(false); setEditingId(null); }} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={saveSource} disabled={!form.name} style={{ padding: '8px 20px', border: 'none', borderRadius: 6, background: '#6366f1', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: form.name ? 1 : 0.5 }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Merge Modal */}
      {showMerge && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 400 }}>
            <h3 style={{ marginBottom: 16 }}>Merge Source</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>All leads from the selected source will be moved to the target source.</p>
            <div><label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Merge Into</label>
              <select style={inputStyle} value={mergeTarget} onChange={e => setMergeTarget(e.target.value)}>
                <option value="">Select target source</option>
                {sources.filter(s => s.id !== showMerge).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowMerge(null); setMergeTarget(''); }} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={mergeSources} disabled={!mergeTarget} style={{ padding: '8px 20px', border: 'none', borderRadius: 6, background: '#f59e0b', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: mergeTarget ? 1 : 0.5 }}>Merge</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
